<template>
  <ContentSection
    :class="!data.color_theme?.value || data.color_theme?.value === 'blue' ? `bg-theme-500 text-white` : `
      bg-theme-200
    `"
    :style="colorTheme"
  >
    <Container>
      <ul
        ref="cardsGrid"
        v-animate-children-in-from-bottom
        class="
          flex grid-cols-1 flex-col gap-x-5 gap-y-12
          md:grid
          sm:grid-cols-[repeat(auto-fill,_minmax(19rem,_1fr))] sm:gap-x-12 sm:gap-y-18
        "
      >
        <li
          v-for="card in data.cards"
          :key="card.id"
          class="group flex flex-col justify-between border-b-2 pb-4 sm:pb-5"
        >
          <div>
            <!-- IMAGE -->
            <div
              class="aspect-[290/195] w-full overflow-hidden rounded-br-[3.125rem] bg-white/10"
              :class="[{ 'py-9 pl-18 pr-22': card.image?.extension === 'svg' }, { 'md:opacity-0 md:transition-opacity md:duration-500 md:ease-in-out md:group-hover:opacity-100': !cardsWrapped }]"
            >
              <AppImage
                v-if="card.image"
                :src="card.image"
                :style="`object-position: ${card.image.focus_css};`"
                :alt="useMapAltText(card.image)"
                class="size-full"
                :class="card.image?.is_image ? 'object-cover' : 'object-contain'"
              />
            </div>

            <div
              class="border-t-2 pr-2 pt-6 max-md:border-t-transparent sm:pt-10"
              :class="[{ 'md:-translate-y-24 md:transition-all md:duration-500 md:ease-in-out md:group-hover:translate-y-0 md:group-hover:border-t-transparent': !cardsWrapped }, { 'md:border-t-transparent': cardsWrapped }]"
            >
              <!-- TITLE -->
              <h3 class="mb-4 typo-label-md sm:typo-label-lg">
                {{ card.title }}
              </h3>

              <!-- TEXT -->
              <p class="mb-5 typo-body-lg sm:mb-6 sm:typo-body-xl">
                {{ card.text }}
              </p>
            </div>
          </div>

          <!-- LINK -->
          <AppLink
            v-if="card.link"
            :to="card.link"
            class="typo-body-xl"
            use-simple-arrow
          >
            {{ card.link_text }}
          </AppLink>
        </li>
      </ul>
    </Container>
  </ContentSection>
</template>

<script lang="ts" setup>
import type { ComponentsMainCardsHoverFragment } from '#gql'

const props = defineProps<{
  data: Enforce<ComponentsMainCardsHoverFragment>
  prevComponent?: string | null
  nextComponent?: string | null
}>()

const colorTheme = useGetColorThemeStylesComponent(props.data.color_theme?.value)

const cardsGrid = ref<HTMLUListElement>()
const cardsWrapped = ref(false)

const debouncedHandleResize = useDebounceFn(() => {
  checkCardsWrapped()
}, 200)

// If the cards are wrapped, the image is visible without hover effect (as on mobile)
async function checkCardsWrapped() {
  await nextTick()

  const grid = cardsGrid.value
  if (!grid) return
  const items = Array.from(grid.children)
  if (items.length < 2) return

  const firstTop = items[0].getBoundingClientRect().top
  cardsWrapped.value = items.some(item => item.getBoundingClientRect().top > firstTop)
}

onMounted(() => {
  checkCardsWrapped()
  window.addEventListener('resize', debouncedHandleResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', debouncedHandleResize)
})
</script>
